import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth, StaffRole } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  FileText, Wrench, Info, LogOut, Loader2, Users, CalendarDays, PoundSterling, Clock, Camera, Building2,
  MessageSquare, Palette, Menu, LayoutGrid, File, Key, Globe, Shield, Wallet, Receipt, CreditCard, Star, FileSignature,
  Search, Plus, Bell, ChevronDown, ExternalLink, UserPlus, Inbox, CornerDownLeft, ChevronRight,
  UserRound, LayoutDashboard, MessagesSquare, MessageSquareText, Download, Repeat, ListChecks, BellRing, type LucideIcon,
} from 'lucide-react';
import { version } from '../../../package.json';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { z } from 'zod';
import { useOrg } from '@/contexts/OrgContext';
import { CUSTOM_DOMAINS_ENABLED } from '@/config/featureFlags';
import { ImpersonationBanner } from '@/components/admin/ImpersonationBanner';
import { AdminViewBanner } from '@/components/layout/AdminViewBanner';
import { BookingSetupPrompt } from '@/components/admin/BookingSetupPrompt';
import { AvatarCropper } from '@/components/admin/AvatarCropper';
import { useCompanyInfoBrief } from '@/hooks/useCompanyInfo';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useTenantWebsiteUrl } from '@/hooks/useTenantWebsiteUrl';
import { requestSiteSignInUrl } from '@/lib/siteSignIn';
import { SubscriptionGate } from '@/components/admin/SubscriptionGate';
import { useAdminInboxCounts, useAdminInboxSeen } from '@/hooks/useAdminInboxCounts';
import { useIsMobile } from '@/hooks/use-mobile';
import { AdminPageTitleContext } from '@/components/admin/ui/page-title';
import { initialsOf } from '@/components/admin/ui/status';
import '@/styles/admin.css';

const DEMO_ORG_ID = 'a90138ee-26a5-4570-97b0-5a1144b9ad85';

const changePasswordSchema = z.object({
  newPassword: z.string().min(6, { message: 'Password must be at least 6 characters' }),
  confirmPassword: z.string().min(6, { message: 'Password must be at least 6 characters' }),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type NavGroup = 'overview' | 'bookings' | 'business' | 'website' | 'settings';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  roles: StaffRole[];
  group: NavGroup;
  /** Extra words the top-bar search should match. */
  keywords?: string;
  /** Live count shown as a pill (owner/supervisor inbox counts). */
  countKey?: 'bookingRequests' | 'newEnquiries' | 'acceptedQuotes';
}

interface CustomPage {
  id: string;
  slug: string;
  title: string;
  is_published: boolean;
}

const NAV_GROUPS: { key: NavGroup; label: string | null }[] = [
  { key: 'overview', label: null },
  { key: 'bookings', label: 'Bookings' },
  { key: 'business', label: 'Business' },
  { key: 'website', label: 'Website' },
  { key: 'settings', label: 'Settings' },
];

// Define which roles can access each nav item
// super_admin = platform founders, owner = org owners, supervisor/cleaner = team members
const mainNavItems: NavItem[] = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, roles: ['super_admin', 'owner', 'supervisor', 'cleaner'], group: 'overview', keywords: 'home overview stats' },
  { to: '/admin/schedule', label: 'Jobs & Calendar', icon: CalendarDays, roles: ['super_admin', 'owner', 'supervisor'], group: 'bookings', keywords: 'schedule dispatch bookings requests', countKey: 'bookingRequests' },
  { to: '/admin/repeat-cleans', label: 'Repeat cleans', icon: Repeat, roles: ['super_admin', 'owner', 'supervisor'], group: 'bookings', keywords: 'recurring regular weekly fortnightly monthly series' },
  { to: '/admin/enquiries', label: 'Enquiries', icon: Inbox, roles: ['super_admin', 'owner', 'supervisor'], group: 'bookings', keywords: 'leads callbacks quotes', countKey: 'newEnquiries' },
  { to: '/admin/quotes', label: 'Quotes', icon: FileSignature, roles: ['super_admin', 'owner', 'supervisor'], group: 'bookings', keywords: 'quote estimate price accept', countKey: 'acceptedQuotes' },
  { to: '/admin/customers', label: 'Customers', icon: UserRound, roles: ['super_admin', 'owner', 'supervisor'], group: 'bookings', keywords: 'clients contacts' },
  { to: '/admin/messages', label: 'Messages', icon: MessageSquare, roles: ['super_admin', 'owner', 'supervisor'], group: 'bookings', keywords: 'chat team inbox' },
  { to: '/admin/jobs-pricing', label: 'Services & Pricing', icon: PoundSterling, roles: ['super_admin', 'owner'], group: 'business', keywords: 'prices products' },
  { to: '/admin/team', label: 'My Team', icon: Users, roles: ['super_admin', 'owner', 'supervisor'], group: 'business', keywords: 'cleaners staff' },
  { to: '/admin/operational-hours', label: 'Operational Hours', icon: Clock, roles: ['super_admin', 'owner'], group: 'business', keywords: 'opening times availability instant booking auto approve confirm' },
  { to: '/admin/invoices', label: 'Invoices', icon: Receipt, roles: ['super_admin', 'owner'], group: 'business', keywords: 'billing finance' },
  { to: '/admin/payments', label: 'Payments', icon: Wallet, roles: ['super_admin', 'owner'], group: 'business', keywords: 'stripe card finance' },
  { to: '/admin/company-info', label: 'Company Info', icon: Building2, roles: ['super_admin', 'owner'], group: 'settings', keywords: 'business details address logo' },
  { to: '/admin/checklists', label: 'Checklists', icon: ListChecks, roles: ['super_admin', 'owner', 'supervisor'], group: 'settings', keywords: 'checklist tasks cleaning list photos completion report' },
  { to: '/admin/text-messages', label: 'Text messages', icon: MessageSquareText, roles: ['super_admin', 'owner'], group: 'settings', keywords: 'sms text messages twilio reminders mobile' },
  { to: '/admin/billing', label: 'Billing & Subscription', icon: CreditCard, roles: ['super_admin', 'owner'], group: 'settings', keywords: 'plan subscription' },
  { to: '/admin/data', label: 'Your data', icon: Download, roles: ['super_admin', 'owner', 'supervisor'], group: 'settings', keywords: 'export download csv gdpr delete account close business' },
];

const websiteNavItems: NavItem[] = [
  // Custom-domain management — see CUSTOM_DOMAINS_ENABLED.
  ...(CUSTOM_DOMAINS_ENABLED
    ? [{ to: '/admin/domain', label: 'Domain', icon: Globe, roles: ['super_admin', 'owner'], group: 'website', keywords: 'custom domain web address dns ssl' } as NavItem]
    : []),
  { to: '/admin/theme', label: 'Theme', icon: Palette, roles: ['super_admin', 'owner'], group: 'website', keywords: 'colours fonts look' },
  { to: '/admin/page-builder', label: 'Page Builder', icon: LayoutGrid, roles: ['super_admin', 'owner'], group: 'website', keywords: 'pages editor' },
  { to: '/admin/homepage', label: 'Homepage', icon: FileText, roles: ['super_admin', 'owner'], group: 'website', keywords: 'hero' },
  { to: '/admin/about', label: 'About Us', icon: Info, roles: ['super_admin', 'owner'], group: 'website' },
  { to: '/admin/services', label: 'Services', icon: Wrench, roles: ['super_admin', 'owner'], group: 'website' },
  { to: '/admin/reviews', label: 'Reviews', icon: Star, roles: ['super_admin', 'owner'], group: 'website', keywords: 'testimonials ratings' },
  { to: '/admin/seo', label: 'Google & SEO', icon: Search, roles: ['super_admin', 'owner'], group: 'website', keywords: 'seo google search ranking sitemap meta title description' },
];

// Cleaner-specific nav items
const cleanerNavItems: NavItem[] = [
  { to: '/admin', label: 'My Calendar', icon: CalendarDays, roles: ['cleaner'], group: 'overview' },
  { to: '/admin/messages', label: 'Messages', icon: MessageSquare, roles: ['cleaner'], group: 'overview' },
];

// Quick-create shortcuts in the top bar "+" menu. Each one opens an existing
// create flow (or the page that hosts it).
const createActions: { to: string; label: string; icon: LucideIcon; roles: StaffRole[] }[] = [
  { to: '/admin/schedule?new=job', label: 'New job', icon: CalendarDays, roles: ['super_admin', 'owner', 'supervisor'] },
  { to: '/admin/quotes/new', label: 'New quote', icon: FileSignature, roles: ['super_admin', 'owner', 'supervisor'] },
  { to: '/admin/invoices', label: 'New invoice', icon: Receipt, roles: ['super_admin', 'owner'] },
  { to: '/admin/team', label: 'Team member', icon: UserPlus, roles: ['super_admin', 'owner'] },
  { to: '/admin/jobs-pricing', label: 'Service', icon: PoundSterling, roles: ['super_admin', 'owner'] },
  { to: '/admin/page-builder', label: 'Website page', icon: File, roles: ['super_admin', 'owner'] },
];

const getRoleLabel = (role: StaffRole) => {
  switch (role) {
    case 'super_admin': return 'Platform Admin';
    case 'owner': return 'Owner';
    case 'supervisor': return 'Supervisor';
    case 'cleaner': return 'Cleaner';
    default: return 'Team Member';
  }
};

// ---------------------------------------------------------------------------
// Top-bar page search: filters the visible nav items and jumps on Enter.
// ---------------------------------------------------------------------------

interface SearchEntry {
  to: string;
  label: string;
  icon: LucideIcon;
  section: string;
  keywords?: string;
}

function AdminNavSearch({ entries, compact = false }: { entries: SearchEntry[]; compact?: boolean }) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q
      ? entries.filter((e) => `${e.label} ${e.section} ${e.keywords ?? ''}`.toLowerCase().includes(q))
      : entries;
    return list.slice(0, 8);
  }, [entries, query]);

  useEffect(() => setActive(0), [query]);

  // ⌘K / Ctrl+K focuses the search from anywhere in the admin.
  useEffect(() => {
    if (compact) return;
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [compact]);

  const go = (entry: SearchEntry | undefined) => {
    if (!entry) return;
    navigate(entry.to);
    setQuery('');
    setOpen(false);
    inputRef.current?.blur();
  };

  return (
    <div className="relative w-full">
      <label className="adm-search">
        <Search className="h-4 w-4 shrink-0" />
        <input
          ref={inputRef}
          type="search"
          value={query}
          placeholder="Search pages…"
          aria-label="Search admin pages"
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActive((i) => Math.min(i + 1, results.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActive((i) => Math.max(i - 1, 0));
            } else if (e.key === 'Enter') {
              e.preventDefault();
              go(results[active]);
            } else if (e.key === 'Escape') {
              setOpen(false);
              inputRef.current?.blur();
            }
          }}
        />
        {!compact && <kbd className="adm-kbd hidden lg:inline">⌘K</kbd>}
      </label>
      {open && (
        <div
          className="absolute left-0 right-0 top-[calc(100%+6px)] z-50 overflow-hidden rounded-xl border border-border bg-popover p-1.5 shadow-[var(--adm-shadow-md)]"
          role="listbox"
          aria-label="Matching admin pages"
        >
          {results.length === 0 ? (
            <p className="px-3 py-4 text-center text-[13px] text-muted-foreground">No pages match “{query}”.</p>
          ) : (
            results.map((entry, index) => {
              const Icon = entry.icon;
              return (
                <button
                  key={`${entry.to}-${entry.label}`}
                  type="button"
                  role="option"
                  aria-selected={index === active}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => go(entry)}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px]',
                    index === active ? 'bg-secondary text-secondary-foreground' : 'text-foreground',
                  )}
                >
                  <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="flex-1 truncate font-medium">{entry.label}</span>
                  <span className="text-[11px] text-muted-foreground">{entry.section}</span>
                  {index === active && <CornerDownLeft className="h-3.5 w-3.5 text-muted-foreground" />}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

const AdminLayout = () => {
  const { user, isSuperAdmin, staffRole, organizationMembership, organizationMemberships, loading, rolesLoading, signOut, updatePassword, switchOrganization } = useAuth();
  const { org } = useOrg();
  const { orgId: adminOrgId } = useAdminOrg();
  const websiteUrl = useTenantWebsiteUrl(adminOrgId);
  // Open the business's site signed in as the owner, so its owner bar ("Edit
  // this page") shows. The tab opens straight away (no pop-up blocking), then
  // goes to the site once the one-time sign-in link is ready.
  const openWebsite = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (!websiteUrl || !adminOrgId) return;
      if (new URL(websiteUrl).origin === window.location.origin) return;
      event.preventDefault();
      const tab = window.open('', '_blank');
      if (!tab) {
        window.open(websiteUrl, '_blank', 'noopener');
        return;
      }
      tab.opener = null;
      void requestSiteSignInUrl(websiteUrl, adminOrgId).then((url) => {
        tab.location.href = url;
      });
    },
    [websiteUrl, adminOrgId],
  );
  // Anchor admin-shell reads (company_info, custom_pages, tenant switcher) to the
  // membership-resolved org. Never fall back to OrgContext's apex/preview public
  // org here — that briefly scoped admin reads to the public fallback org before
  // the membership landed (issue 6 regression).
  const activeAdminOrgId = adminOrgId || organizationMembership?.organizationId;
  const navigate = useNavigate();
  const location = useLocation();
  const [profileName, setProfileName] = useState<string | null>(null);
  const [profilePhone, setProfilePhone] = useState<string | null>(null);
  const [profileAvatar, setProfileAvatar] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [avatarCropperOpen, setAvatarCropperOpen] = useState(false);
  const [pendingAvatarSrc, setPendingAvatarSrc] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [customPages, setCustomPages] = useState<CustomPage[]>([]);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [savingPassword, setSavingPassword] = useState(false);
  // Title a page registered via useAdminPageTitle (rendered as the top-bar h1).
  const [pageTitle, setPageTitle] = useState<string | null>(null);
  const registerPageTitle = useCallback((title: string | null) => setPageTitle(title), []);
  // Render the create/account menus once (mobile row OR desktop row) so the DOM
  // never holds a hidden duplicate of them.
  const isMobile = useIsMobile();

  const hasAccess = staffRole !== null;
  const isCleaner = staffRole === 'cleaner';
  const { data: adminCompanyInfo } = useCompanyInfoBrief(activeAdminOrgId);
  const sidebarCompanyName = adminCompanyInfo?.company_name || organizationMembership?.organizationName || org.name;
  const { data: inboxCounts } = useAdminInboxCounts(activeAdminOrgId, hasAccess && !isCleaner);
  // Badge = items not yet seen; opening the bell marks them read.
  const { unseenCount: notificationUnseen, markSeen: markNotificationsSeen } = useAdminInboxSeen(user?.id, activeAdminOrgId, inboxCounts);

  // Radix portals (dialogs, selects, popovers) render on <body>, outside the
  // .admin-shell root — flag the body so they pick up the admin palette too.
  useEffect(() => {
    document.body.classList.add('admin-scope');
    return () => document.body.classList.remove('admin-scope');
  }, []);

  useEffect(() => {
    // Demo single-org mode: no setup wizard / register-business redirect.
    // Only enforce login + admin access.
    if (!loading && !rolesLoading) {
      if (!user) {
        navigate('/auth');
      }
    }
  }, [user, loading, rolesLoading, navigate]);

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      const { data } = await supabase
        .from('profiles')
        .select('name, phone, avatar_url')
        .eq('id', user.id)
        .maybeSingle();
      if (data) {
        setProfileName(data.name);
        setProfilePhone(data.phone);
        setProfileAvatar(data.avatar_url);
        setEditName(data.name || '');
        setEditPhone(data.phone || '');
      }
    };
    fetchProfile();
  }, [user]);

  useEffect(() => {
    const fetchCustomPages = async () => {
      if (!activeAdminOrgId) return;
      const { data } = await supabase
        .from('custom_pages')
        .select('id, slug, title, is_published')
        .eq('organization_id', activeAdminOrgId)
        .order('sort_order', { ascending: true });
      if (data) {
        setCustomPages(data);
      }
    };
    fetchCustomPages();
  }, [activeAdminOrgId]);

  // Close mobile menu when route changes - must be before early returns
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    if (pendingAvatarSrc) URL.revokeObjectURL(pendingAvatarSrc);
    setPendingAvatarSrc(URL.createObjectURL(file));
    setAvatarCropperOpen(true);
    // Reset the input so re-selecting the same file still fires onChange.
    e.target.value = '';
  };

  const handleAvatarCropperOpenChange = (open: boolean) => {
    setAvatarCropperOpen(open);
    if (!open && pendingAvatarSrc) {
      URL.revokeObjectURL(pendingAvatarSrc);
      setPendingAvatarSrc(null);
    }
  };

  const handleAvatarCropComplete = async (avatarUrl: string) => {
    if (!user) return;

    setUploading(true);
    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          email: user.email ?? null,
          avatar_url: avatarUrl,
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setProfileAvatar(avatarUrl);
      handleAvatarCropperOpenChange(false);
      toast.success('Avatar updated');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to upload avatar: ${message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!user) return;
    setSaving(true);
    const { data, error } = await supabase
      .from('profiles')
      .upsert({
        id: user.id,
        email: user.email ?? null,
        name: editName.trim() || null,
        phone: editPhone.trim() || null,
      }, { onConflict: 'id' })
      .select('name, phone, avatar_url')
      .single();
    setSaving(false);
    if (error) {
      toast.error(`Failed to update profile: ${error.message}`);
    } else {
      setProfileName(data.name);
      setProfilePhone(data.phone);
      setProfileAvatar(data.avatar_url);
      setEditName(data.name || '');
      setEditPhone(data.phone || '');
      setEditDialogOpen(false);
      toast.success('Profile updated');
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  const handleChangePassword = async () => {
    setPasswordError(null);

    const result = changePasswordSchema.safeParse({ newPassword, confirmPassword });
    if (!result.success) {
      setPasswordError(result.error.errors[0].message);
      return;
    }

    setSavingPassword(true);
    try {
      const { error } = await updatePassword(newPassword);
      if (error) {
        toast.error('Failed to update password: ' + error.message);
      } else {
        toast.success('Password updated successfully');
        setChangePasswordDialogOpen(false);
        setNewPassword('');
        setConfirmPassword('');
      }
    } finally {
      setSavingPassword(false);
    }
  };

  const handleTenantChange = (nextOrgId: string) => {
    switchOrganization(nextOrgId);
    toast.success('Tenant changed');
  };

  // Filter nav items based on user's role
  const filterNavItems = (items: NavItem[]) => {
    return items.filter(item => staffRole && item.roles.includes(staffRole));
  };

  const visibleMainNavItems = isCleaner ? cleanerNavItems : filterNavItems(mainNavItems);
  const visibleWebsiteNavItems = filterNavItems(websiteNavItems);
  const visibleNavItems = [...visibleMainNavItems, ...visibleWebsiteNavItems];
  const showCustomPages = customPages.length > 0 && (staffRole === 'super_admin' || staffRole === 'owner');
  const visibleCreateActions = isCleaner ? [] : createActions.filter((a) => staffRole && a.roles.includes(staffRole));

  const groupLabel = (group: NavGroup) => NAV_GROUPS.find((g) => g.key === group)?.label ?? 'Overview';
  const currentNavItem = visibleNavItems.find((item) => item.to === location.pathname);
  const topbarFallbackTitle = currentNavItem?.label ?? (isCleaner ? 'My Dashboard' : 'Admin');
  const topbarSection = currentNavItem ? groupLabel(currentNavItem.group) : null;
  const notificationTotal = (inboxCounts?.bookingRequests ?? 0) + (inboxCounts?.newEnquiries ?? 0) + (inboxCounts?.acceptedQuotes ?? 0) + (inboxCounts?.followUpsDue ?? 0);

  const searchEntries: SearchEntry[] = [
    ...visibleNavItems.map((item) => ({
      to: item.to,
      label: item.label,
      icon: item.icon,
      section: groupLabel(item.group),
      keywords: item.keywords,
    })),
    ...(showCustomPages
      ? customPages.map((page) => ({ to: `/admin/page-builder?page=${page.slug}`, label: page.title, icon: File, section: 'Custom pages' }))
      : []),
  ];

  // IMPORTANT: Do NOT return an early loader here! Replacing the entire tree causes
  // child components (like PageBuilder with its iframe) to unmount and remount,
  // creating reload loops. Instead, we overlay a loader below while keeping the layout stable.

  const isLoading = loading || rolesLoading;

  // Initial auth/roles hydration resolves asynchronously, so there's a brief window
  // where `user` is null (getSession pending) or `staffRole` hasn't loaded yet while
  // `isLoading` is true. Render a loader during that window instead of the signed-out
  // "Session required" / "Access required" screens, which otherwise flash on /admin
  // during initial auth loading.
  const authLoadingScreen = (
    <div className="admin-shell min-h-screen flex items-center justify-center" data-testid="admin-auth-loading">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );

  // Never render null here; show a deterministic screen to avoid "loads then disappears".
  if (!user) {
    if (isLoading) return authLoadingScreen;
    return (
      <div className="admin-shell min-h-screen flex items-center justify-center p-6">
        <div className="adm-panel max-w-md w-full p-6">
          <h2 className="text-lg font-display text-foreground">Session required</h2>
          <p className="text-sm text-muted-foreground mt-2">
            Your session isn’t available on this page. Please sign in again.
          </p>
          <div className="mt-4">
            <Button className="w-full" onClick={() => navigate('/auth')}>
              Go to sign in
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!hasAccess && !isSuperAdmin) {
    if (isLoading) return authLoadingScreen;
    return (
      <div className="admin-shell min-h-screen flex items-center justify-center p-6">
        <div className="adm-panel max-w-md w-full p-6">
          <h2 className="text-lg font-display text-foreground">Access required</h2>
          <p className="text-sm text-muted-foreground mt-2">
            Your account is signed in, but it doesn't have admin access yet.
          </p>
          <div className="mt-4">
            <Button variant="outline" className="w-full" onClick={handleSignOut}>
              Sign out
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const debugEnabled = new URLSearchParams(location.search).get('debug') === '1';
  const displayName = profileName || user?.email?.split('@')[0] || 'Account';
  const avatarInitial = profileName?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase();

  const renderNavLink = (item: NavItem) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.to;
    const count = item.countKey ? inboxCounts?.[item.countKey] ?? 0 : 0;
    return (
      <Link
        key={`${item.group}-${item.to}`}
        to={item.to}
        aria-current={isActive ? 'page' : undefined}
        className="adm-nav-item"
      >
        <Icon />
        <span className="truncate">{item.label}</span>
        {count > 0 && <span className="adm-nav-count">{count}</span>}
      </Link>
    );
  };

  const SidebarContent = () => (
    <>
      {/* Business (tenant) header */}
      <div className="flex h-16 shrink-0 items-center gap-2.5 px-4" data-testid="admin-sidebar-tenant">
        <TenantMark name={sidebarCompanyName} logoUrl={adminCompanyInfo?.logo_url} />
        <div className="min-w-0 leading-tight">
          <p className="truncate text-[15px] font-semibold tracking-tight text-foreground" title={sidebarCompanyName}>
            {sidebarCompanyName}
          </p>
          <p className="truncate text-[11.5px] font-medium text-muted-foreground">
            {getRoleLabel(staffRole)} workspace
          </p>
        </div>
      </div>

      {organizationMemberships.length > 1 && (
        <div className="space-y-1 px-3 pb-2">
          <Label className="adm-label px-1">Switch business</Label>
          <Select value={activeAdminOrgId} onValueChange={handleTenantChange}>
            <SelectTrigger className="h-8 w-full bg-card text-[13px]" aria-label="Switch business">
              <SelectValue placeholder="Choose tenant" />
            </SelectTrigger>
            <SelectContent>
              {organizationMemberships.map((membership) => (
                <SelectItem key={membership.organizationId} value={membership.organizationId}>
                  {membership.organizationName}{membership.organizationId === DEMO_ORG_ID ? ' (demo)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <nav className="adm-scroll flex-1 space-y-4 overflow-y-auto px-3 pb-4 pt-2" aria-label="Admin">
        {NAV_GROUPS.map((group) => {
          const items = visibleNavItems.filter((item) => item.group === group.key);
          if (items.length === 0) return null;
          return (
            <div key={group.key} className="space-y-0.5">
              {group.label && <p className="adm-label px-2.5 pb-1.5 pt-1">{group.label}</p>}
              {items.map(renderNavLink)}
            </div>
          );
        })}

        {showCustomPages && (
          <div className="space-y-0.5">
            <p className="adm-label px-2.5 pb-1.5 pt-1">Custom Pages</p>
            {customPages.map((page) => {
              const isActive = location.pathname === `/admin/page-builder` && location.search.includes(page.slug);
              return (
                <Link
                  key={page.id}
                  to={`/admin/page-builder?page=${page.slug}`}
                  aria-current={isActive ? 'page' : undefined}
                  className="adm-nav-item"
                >
                  <File />
                  <span className="flex-1 truncate">{page.title}</span>
                  {!page.is_published && <span className="adm-chip adm-chip--grey">Draft</span>}
                </Link>
              );
            })}
          </div>
        )}
      </nav>

      <div className="space-y-0.5 border-t border-border px-3 py-3">
        {/* The business's own site, never the sales site the admin may be on. */}
        {websiteUrl ? (
          <a href={websiteUrl} target="_blank" rel="noopener noreferrer" className="adm-nav-item" onClick={openWebsite}>
            <ExternalLink />
            View Website
          </a>
        ) : (
          <Link to="/" className="adm-nav-item">
            <ExternalLink />
            View Website
          </Link>
        )}
        {isSuperAdmin && (
          <Link to="/platform" className="adm-nav-item">
            <Shield />
            Platform
          </Link>
        )}
        <button type="button" className="adm-nav-item w-full" onClick={handleSignOut}>
          <LogOut />
          Sign Out
        </button>
        <p className="px-2.5 pt-1 text-[10.5px] text-muted-foreground/60">v{version}</p>
      </div>
    </>
  );

  const accountMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Account menu for ${displayName}`}
          className="flex items-center gap-2.5 rounded-full py-1 pl-1 pr-1 transition-colors hover:bg-[hsl(var(--adm-hover))] md:pr-2.5"
        >
          <Avatar className="h-8 w-8">
            <AvatarImage src={profileAvatar || undefined} />
            <AvatarFallback className="bg-secondary text-[12px] font-semibold text-secondary-foreground">
              {avatarInitial}
            </AvatarFallback>
          </Avatar>
          <span className="hidden min-w-0 text-left leading-tight lg:block">
            <span className="block max-w-[140px] truncate text-[13px] font-semibold text-foreground">{displayName}</span>
            <span className="block text-[11px] text-muted-foreground">{getRoleLabel(staffRole)}</span>
          </span>
          <ChevronDown className="hidden h-4 w-4 text-muted-foreground md:block" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="font-normal">
          <p className="truncate text-[13px] font-semibold">{profileName || 'No name set'}</p>
          <p className="truncate text-[12px] text-muted-foreground">{user?.email}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => setEditDialogOpen(true)}>
          <UserRound className="mr-2 h-4 w-4" />
          Edit profile
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => setChangePasswordDialogOpen(true)}>
          <Key className="mr-2 h-4 w-4" />
          Change password
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={handleSignOut}>
          <LogOut className="mr-2 h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const createMenu = visibleCreateActions.length > 0 ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="adm-create-btn" aria-label="Create new">
          <Plus />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuLabel className="adm-label">Create</DropdownMenuLabel>
        {visibleCreateActions.map((action) => {
          const Icon = action.icon;
          return (
            <DropdownMenuItem key={action.label} onSelect={() => navigate(action.to)}>
              <Icon className="mr-2 h-4 w-4 text-muted-foreground" />
              {action.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  ) : null;

  const notificationItems = [
    { key: 'bookingRequests', count: inboxCounts?.bookingRequests ?? 0, to: '/admin/schedule', icon: CalendarDays, label: 'Booking requests awaiting approval' },
    { key: 'newEnquiries', count: inboxCounts?.newEnquiries ?? 0, to: '/admin/enquiries', icon: Inbox, label: 'New enquiries to follow up' },
    { key: 'acceptedQuotes', count: inboxCounts?.acceptedQuotes ?? 0, to: '/admin/quotes', icon: FileSignature, label: 'Accepted quotes to book in' },
    { key: 'followUpsDue', count: inboxCounts?.followUpsDue ?? 0, to: '/admin/enquiries?filter=follow-ups', icon: BellRing, label: 'Leads to follow up today' },
  ];

  const notificationsMenu = !isCleaner ? (
    <Popover onOpenChange={(open) => { if (open) markNotificationsSeen(); }}>
      <PopoverTrigger asChild>
        <button type="button" className="adm-icon-btn" aria-label={`Alerts${notificationUnseen ? `: ${notificationUnseen} new` : ''}`}>
          <Bell />
          {notificationUnseen > 0 && <span className="adm-dot-badge">{notificationUnseen > 9 ? '9+' : notificationUnseen}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-2">
        <p className="adm-label px-2 pb-2 pt-1">Needs attention</p>
        {notificationTotal === 0 ? (
          <p className="px-2 pb-3 text-[13px] text-muted-foreground">You're all caught up.</p>
        ) : (
          notificationItems.filter((n) => n.count > 0).map((n) => {
            const Icon = n.icon;
            return (
              <Link
                key={n.key}
                to={n.to}
                className="flex items-center gap-3 rounded-lg px-2 py-2 text-[13px] hover:bg-muted"
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-secondary text-secondary-foreground">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="flex-1">
                  <span className="block font-semibold text-foreground">{n.count} {n.count === 1 ? 'item' : 'items'}</span>
                  <span className="block text-[12px] text-muted-foreground">{n.label}</span>
                </span>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </Link>
            );
          })
        )}
      </PopoverContent>
    </Popover>
  ) : null;

  // Deliberately a button (not a link named "Messages") so it never collides
  // with the sidebar's Messages nav link.
  const inboxButton = (
    <button type="button" className="adm-icon-btn" aria-label="Open team inbox" onClick={() => navigate('/admin/messages')}>
      <MessagesSquare />
    </button>
  );

  const TitleTag = pageTitle ? 'h1' : 'p';

  const isVisualEditorRoute = location.pathname.startsWith('/admin/pages') || location.pathname.startsWith('/admin/page-builder');

  // For visual editor routes, we ALWAYS render the layout with an overlay for auth states
  // to prevent unmounting the iframe and causing reload loops
  if (isVisualEditorRoute) {
    // Render blocking overlays instead of early returns
    const blockingOverlay = isLoading ? (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-background/95 backdrop-blur-sm">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    ) : !user ? (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-background p-6">
        <div className="max-w-md w-full bg-card border border-border rounded-xl p-6">
          <h2 className="text-lg font-display text-foreground">Session required</h2>
          <p className="text-sm text-muted-foreground mt-2">
            Your session isn't available on this page. Please sign in again.
          </p>
          <div className="mt-4">
            <Button className="w-full" onClick={() => navigate('/auth')}>
              Go to sign in
            </Button>
          </div>
        </div>
      </div>
    ) : (!hasAccess && !isSuperAdmin) ? (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-background p-6">
        <div className="max-w-md w-full bg-card border border-border rounded-xl p-6">
          <h2 className="text-lg font-display text-foreground">Access required</h2>
          <p className="text-sm text-muted-foreground mt-2">
            Your account is signed in, but it doesn't have admin access yet.
          </p>
          <div className="mt-4">
            <Button variant="outline" className="w-full" onClick={handleSignOut}>
              Sign out
            </Button>
          </div>
        </div>
      </div>
    ) : null;

    return (
      <div className="admin-shell min-h-screen bg-muted">
        {blockingOverlay}
        {debugEnabled && (
          <div className="fixed bottom-3 right-3 z-[1000] max-w-[420px] rounded-lg border border-border bg-card p-3 text-xs">
            <div className="font-medium text-foreground">Admin Debug</div>
            <div className="mt-1 text-muted-foreground">route: {location.pathname}{location.search}</div>
            <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1">
              <div>loading: {String(loading)}</div>
              <div>rolesLoading: {String(rolesLoading)}</div>
              <div>staffRole: {String(staffRole)}</div>
              <div>isSuperAdmin: {String(isSuperAdmin)}</div>
              <div className="col-span-2 truncate">user: {user?.email ?? 'null'}</div>
            </div>
          </div>
        )}
        <main className="h-screen overflow-hidden">
          <ImpersonationBanner />
          <AdminViewBanner />
          <AdminPageTitleContext.Provider value={registerPageTitle}>
            <SubscriptionGate>
              <Outlet />
            </SubscriptionGate>
          </AdminPageTitleContext.Provider>
        </main>
      </div>
    );
  }

  return (
    <div className="admin-shell min-h-screen md:flex">
      {debugEnabled && (
        <div className="fixed bottom-3 right-3 z-[1000] max-w-[420px] rounded-lg border border-border bg-card p-3 text-xs">
          <div className="font-medium text-foreground">Admin Debug</div>
          <div className="mt-1 text-muted-foreground">route: {location.pathname}{location.search}</div>
          <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1">
            <div>loading: {String(loading)}</div>
            <div>rolesLoading: {String(rolesLoading)}</div>
            <div>staffRole: {String(staffRole)}</div>
            <div>isSuperAdmin: {String(isSuperAdmin)}</div>
            <div className="col-span-2 truncate">user: {user?.email ?? 'null'}</div>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="adm-sidebar sticky top-0 hidden h-screen w-[248px] shrink-0 flex-col md:flex">
        <SidebarContent />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Top bar */}
        <header className="adm-topbar sticky top-0 z-40">
          {/* Mobile brand row (rendered only on phones so its company name and
              menus never duplicate the desktop sidebar/top bar in the DOM) */}
          {isMobile && (
          <div className="flex h-14 items-center gap-2 border-b border-border px-3 md:hidden">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <button type="button" className="adm-icon-btn" aria-label="Open admin navigation">
                  <Menu />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="admin-shell flex w-[280px] flex-col p-0 adm-sidebar">
                <SidebarContent />
              </SheetContent>
            </Sheet>
            <TenantMark name={sidebarCompanyName} logoUrl={adminCompanyInfo?.logo_url} size="sm" />
            <div className="min-w-0 flex-1 leading-tight">
              <p className="truncate text-[13px] font-semibold text-foreground">{sidebarCompanyName}</p>
              <h2 className="text-[11px] font-medium text-muted-foreground">
                {isCleaner ? 'My Dashboard' : 'Admin Panel'}
              </h2>
            </div>
            {createMenu}
            {accountMenu}
          </div>
          )}

          {/* Title / search / actions row */}
          <div className="flex h-14 items-center gap-3 px-4 md:h-16 md:px-6 lg:px-8">
            <div className="min-w-0 shrink-0 leading-tight">
              {topbarSection && <p className="adm-label hidden md:block">{topbarSection}</p>}
              <TitleTag className="truncate text-[16px] font-semibold tracking-tight text-foreground md:text-[18px]">
                {pageTitle ?? topbarFallbackTitle}
              </TitleTag>
            </div>
            <div className="hidden w-full max-w-[300px] md:ml-6 md:block lg:ml-10 lg:max-w-[380px]">
              <AdminNavSearch entries={searchEntries} />
            </div>
            <div className="ml-auto flex items-center gap-1">
              {!isMobile && <div className="hidden md:block">{createMenu}</div>}
              {notificationsMenu}
              {inboxButton}
              <div className="mx-1 hidden h-6 w-px bg-border md:block" aria-hidden="true" />
              {!isMobile && <div className="hidden md:block">{accountMenu}</div>}
            </div>
          </div>
        </header>

        {/* Main content */}
        {/* The old purple "Admin view / View your site" strip is superseded by
            the shell itself (sidebar "View Website" link). */}
        <main className="min-w-0 flex-1">
          <ImpersonationBanner />
          <BookingSetupPrompt />
          <AdminPageTitleContext.Provider value={registerPageTitle}>
            <SubscriptionGate>
              <Outlet />
            </SubscriptionGate>
          </AdminPageTitleContext.Provider>
        </main>
      </div>

      {/* Edit Profile Dialog - outside SidebarContent so typing does not remount the input */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Profile</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="flex flex-col items-center gap-3">
              <Avatar className="h-20 w-20">
                <AvatarImage src={profileAvatar || undefined} />
                <AvatarFallback className="bg-primary/10 text-primary text-2xl">
                  {avatarInitial}
                </AvatarFallback>
              </Avatar>
              <Label htmlFor="avatar-upload" className="cursor-pointer">
                <div className="flex items-center gap-2 text-sm text-primary hover:underline">
                  <Camera className="h-4 w-4" />
                  {uploading ? 'Uploading...' : 'Change Photo'}
                </div>
                <input
                  id="avatar-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarUpload}
                  disabled={uploading}
                />
              </Label>
            </div>
            <div className="space-y-2">
              <Label htmlFor="profile-email">Email</Label>
              <Input
                id="profile-email"
                value={user?.email || ''}
                disabled
                className="bg-muted"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Enter your name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={editPhone}
                onChange={(e) => setEditPhone(e.target.value)}
                placeholder="Enter your phone number"
              />
            </div>
            <Button onClick={handleSaveProfile} disabled={saving} className="w-full">
              {saving ? 'Saving...' : 'Save Profile'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {pendingAvatarSrc && (
        <AvatarCropper
          open={avatarCropperOpen}
          onOpenChange={handleAvatarCropperOpenChange}
          imageSrc={pendingAvatarSrc}
          onCropComplete={handleAvatarCropComplete}
          saving={uploading}
        />
      )}

      {/* Change Password Dialog - moved outside SidebarContent to prevent remounting */}
      <Dialog open={changePasswordDialogOpen} onOpenChange={(open) => {
        setChangePasswordDialogOpen(open);
        if (!open) {
          setNewPassword('');
          setConfirmPassword('');
          setPasswordError(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>
            {passwordError && (
              <p className="text-sm text-destructive">{passwordError}</p>
            )}
            <Button onClick={handleChangePassword} disabled={savingPassword} className="w-full">
              {savingPassword ? 'Updating...' : 'Update Password'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLayout;

/** The business's logo, or its initials on the brand gradient when no logo is set. */
function TenantMark({ name, logoUrl, size = 'md' }: { name: string; logoUrl?: string | null; size?: 'sm' | 'md' }) {
  const [failed, setFailed] = useState(false);
  const px = size === 'sm' ? 28 : 36;
  const dims = { width: px, height: px, borderRadius: size === 'sm' ? 8 : 10 };
  if (logoUrl && !failed) {
    // The logo at its own shape (same height as the badge), not squeezed into a square.
    return (
      <img
        src={logoUrl}
        alt=""
        aria-hidden="true"
        className="shrink-0 object-contain"
        style={{ height: px, width: 'auto', maxWidth: size === 'sm' ? 84 : 112 }}
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <span className="adm-brand-mark" style={{ ...dims, fontSize: size === 'sm' ? 11 : 13 }} aria-hidden="true">
      {initialsOf(name)}
    </span>
  );
}
